import { FC, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, useDialog } from 'components';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useTemplates } from 'contexts';
import { Booking, BookingDetails } from 'types';

const getDetails = (booking: Partial<Booking>): string => {
  return Object.assign([], booking.bookingDetails)
    .reduce((acc, { count, iso }: BookingDetails) => {
      return [...acc, ...Array(Number(!!count && !!iso)).fill(`${count} x ${iso}`)];
    }, [] as string[])
    .join(', ');
};

const Template: FC<{ name: string; booking: Partial<Booking> }> = p => {
  const navigate = useNavigate();

  return (
    <div className="card-primary raised h-100">
      <div className="card-header justify-content-between align-items-center">
        <h3>{p.name}</h3>
        <div className="d-flex justify-content-between align-items-center">
          <button className="btn-back">
            <span className="material-icons icon-large">add_circle</span>
          </button>
          <button className="btn-back margin-left-16px" onClick={() => navigate(`/templates/${p.name}`)}>
            <span className="material-icons icon-large" data-cy={`template-card-header-edit-button-${p.name}`}>edit</span>
          </button>
        </div>
      </div>
      <div className="card-body pointer" onClick={() => navigate(`/bookings/new/${p.name}`)} data-cy={`template-card-body-${p.name}`}>
        <div className="grid-2">
          <label>Customer</label>
          <p>{p.booking.customer?.name}</p>
          <label>Location</label>
          <p>{p.booking.location?.name}</p>
          <label>Date Created</label>
          <p>{!p.booking.createdDate ? '' : new Date(p.booking.createdDate).toLocaleDateString()}</p>
          <label>Equipment</label>
          <p>{getDetails(p.booking)}</p>
        </div>
      </div>
    </div>
  );
};

const Templates = () => {
  const { templates } = useTemplates();
  const { closeDialog, openDialog, ref } = useDialog();
  const navigate = useNavigate();
  const [name, setName] = useState('');

  const handleSubmit = useCallback(() => {
    navigate('/templates/' + name);
  }, [name, navigate]);

  return (
    <>
      <div className="page">
        <div className="page-header">
          <h1>TEMPLATES</h1>
          <button className="btn-outline" type="button" data-cy="new-template-button" onClick={openDialog}>
            NEW TEMPLATE
          </button>
        </div>
        <div>
          <p className="mt-1 mb-4">Click on a template title to create a new booking using that template.</p>
        </div>
        {/* Needs to be updated */}
        <div className="grid-auto-template">
          {templates?.map(({ name, ...template }) => (
            <Template name={name || ''} booking={template} key={name} />
          ))}
        </div>
      </div>
      <Dialog closeDialog={closeDialog} ref={ref}>
        <div data-cy="new-template-dialog" className="grid-1 auto-rows">
          <h3 className="text-primary-dark">Add New Template</h3>
          <input
            className="align-self-center w-100"
            data-cy="new-template-dialog-name-input"
            placeholder="TEMPLATE NAME"
            value={name}
            onChange={e => setName(e.target.value.toUpperCase())}
          />
          <div className="grid-3 align-self-end">
            <button className="btn-outline col-start-2" onClick={closeDialog}>
              Cancel
            </button>
            <button
              data-cy="new-template-dialog-next-button"
              className="btn-primary col-start-3"
              disabled={!name.length}
              onClick={handleSubmit}
            >
              Next
            </button>
          </div>
        </div>
      </Dialog>
      <SuccessDialog title="Template Saved" />
    </>
  );
};

export default Templates;
